import api from './api';
import { CLOUD_API_URL } from '../config/serverConfig';
import { isSessionDead } from '../lib/sessionDead';
import { shouldRouteManageToCheckout } from '../utils/billingEntitlement';

export const subscriptionService = {
  getStatus: () => api.get('/api/stripe/subscription-status'),
  createCheckoutSession: (uiMode: string = 'embedded', cadence: string = 'monthly') =>
    api.post('/api/stripe/create-checkout-session', { ui_mode: uiMode, cadence }),
  createPortalSession: () => api.post('/api/stripe/create-billing-portal-session'),
};

function getToken(): string | null {
  try {
    const s = localStorage.getItem('maestro_auth');
    return s ? JSON.parse(s).accessToken : null;
  } catch { return null; }
}

function getUserId(): string | null {
  try {
    const token = getToken();
    if (!token) return null;
    const payload = JSON.parse(atob(token.split('.')[1]));
    return payload.sub || null;
  } catch { return null; }
}

export type BillingPortalResult =
  | { status: 'portal'; url: string }
  | { status: 'checkout'; message?: string }
  | { status: 'error'; message: string; requestId?: string };

export type UndoCancelResult =
  | { ok: true }
  | { ok: false; error: string; requiresCheckout?: boolean };

export async function undoCancelSubscription(): Promise<UndoCancelResult> {
  const token = getToken();
  if (!token) {
    return { ok: false, error: 'Please sign in to manage billing.' };
  }
  try {
    const resp = await fetch(`${CLOUD_API_URL}/api/stripe/reactivate`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
    });
    const data = await resp.json().catch(() => ({})) as {
      error?: string;
      requires_checkout?: boolean;
    };
    if (resp.ok) return { ok: true };
    return {
      ok: false,
      error: data.error || `Unable to resume subscription (${resp.status}).`,
      requiresCheckout: data.requires_checkout === true || resp.status === 409,
    };
  } catch {
    return { ok: false, error: 'Unable to reach the billing server. Please check your connection.' };
  }
}

export function formatBillingError(
  data: { error?: string; message?: string; request_id?: string },
  fallback: string,
): string {
  const msg = data.error || data.message || fallback;
  return data.request_id ? `${msg} (Ref: ${data.request_id.slice(0, 8)})` : msg;
}

export async function openBillingPortal(): Promise<BillingPortalResult> {
  const token = getToken();
  if (!token) {
    return { status: 'error', message: 'Please sign in to manage billing.' };
  }
  try {
    const statusResp = await fetch(`${CLOUD_API_URL}/api/stripe/subscription-status`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (statusResp.ok) {
      const sub = await statusResp.json() as {
        resubscribe_via_checkout?: boolean;
        subscribe_via_checkout?: boolean;
        subscription_tier?: string;
        stripe_customer_id?: string | null;
        stripe_subscription_id?: string | null;
        has_stripe_subscription?: boolean;
        has_payment_method?: boolean;
      };
      if (shouldRouteManageToCheckout(sub)) {
        return {
          status: 'checkout',
          message: sub.resubscribe_via_checkout
            ? 'Subscribe to reactivate your Maestro account.'
            : 'Complete checkout to add a payment method and activate paid billing.',
        };
      }
    }

    const resp = await fetch(`${CLOUD_API_URL}/api/stripe/create-billing-portal-session`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      credentials: 'omit',
    });
    const data = await resp.json().catch(() => ({})) as {
      url?: string;
      error?: string;
      requires_checkout?: boolean;
      request_id?: string;
    };
    if (resp.ok && data?.url) {
      return { status: 'portal', url: data.url };
    }
    if (data.requires_checkout || resp.status === 409) {
      return {
        status: 'checkout',
        message: data.error || 'Your subscription is not active yet. Subscribe to add a payment method.',
      };
    }
    return {
      status: 'error',
      message: formatBillingError(data, 'Unable to open billing portal. Please try again.'),
      requestId: data.request_id,
    };
  } catch {
    return { status: 'error', message: 'Unable to reach the billing server. Please check your connection.' };
  }
}

export const billingService = {
  async switchToAnnual(): Promise<{ status: string; current_period_end?: number; error?: string } | null> {
    const token = getToken();
    if (!token) return null;
    try {
      const resp = await fetch(`${CLOUD_API_URL}/api/maestro/billing/switch-to-annual`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      });
      return await resp.json() as any;
    } catch { return null; }
  },

  async switchToMonthly(): Promise<{ status: string; current_period_end?: number; error?: string } | null> {
    const token = getToken();
    if (!token) return null;
    try {
      const resp = await fetch(`${CLOUD_API_URL}/api/maestro/billing/switch-to-monthly`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      });
      return await resp.json() as any;
    } catch { return null; }
  },

  async getStripeSessionUrl(): Promise<BillingPortalResult> {
    return openBillingPortal();
  },

  async getBillingStatus(): Promise<{ status: string; trial_days_remaining: number } | null> {
    if (isSessionDead()) return null;
    const userId = getUserId();
    const token = getToken();
    if (!userId || !token) return null;
    const resp = await fetch(`${CLOUD_API_URL}/api/maestro/billing/status?userId=${userId}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!resp.ok) return null;
    return await resp.json() as any;
  },

  async cancelAccount(): Promise<{ ok: boolean; error?: string; subscription_ends_at?: string | null; method?: string | null }> {
    const token = getToken();
    if (!token) return { ok: false, error: 'Please sign in again.' };
    const resp = await fetch(`${CLOUD_API_URL}/api/stripe/cancel-at-period-end`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
    });
    const data = await resp.json().catch(() => ({})) as Record<string, unknown>;
    if (resp.ok) {
      return {
        ok: true,
        subscription_ends_at: (data.subscription_ends_at as string | null) ?? null,
        method: (data.method as string | undefined) ?? null,
      };
    }
    return {
      ok: false,
      error: (data.error as string | undefined) || `Cancellation failed (${resp.status}). Please try again or contact support.`,
    };
  },
};
